
import { LandingNav } from "@/components/landing/LandingNav";
import { SEO } from "@/components/SEO";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Trophy, Gift, Share2, Users } from "lucide-react";

import CreateContest from "@/components/landing/CreateContest";
import ShareYourLink from "@/components/landing/ShareYourLink";
import EntriesRollingIn from "@/components/landing/EntriesRollingIn";
import WinnerSelection from "@/components/landing/WinnerSelection";

const HowItWorks = () => {
    useEffect(() => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'schema-howto';
        script.text = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "How to Run a Giveaway with RaffleKit",
            "description": "Learn the four simple steps to launch a viral giveaway, collect entries, and select a winner fairly.",
            "step": [
                {
                    "@type": "HowToStep",
                    "url": "https://rafflekit.app/how-it-works#step1",
                    "name": "Create your contest",
                    "text": "Set up your prize details, entry methods, and contest dates in just a few clicks. Choose from entry actions like visiting a webpage or referring friends."
                },
                {
                    "@type": "HowToStep",
                    "url": "https://rafflekit.app/how-it-works#step2",
                    "name": "Share your link",
                    "text": "Publish your contest and share your unique landing page link, or embed the widget directly onto your own website."
                },
                {
                    "@type": "HowToStep",
                    "url": "https://rafflekit.app/how-it-works#step3",
                    "name": "Collect entries",
                    "text": "Watch entries roll in as participants complete actions and refer friends for bonus entries. Everything is securely verified."
                },
                {
                    "@type": "HowToStep",
                    "url": "https://rafflekit.app/how-it-works#step4",
                    "name": "Pick a winner",
                    "text": "When the contest ends, randomly select a winner from all valid entries and export your participant list."
                }
            ]
        });

        const existing = document.getElementById('schema-howto');
        if (existing) existing.remove();

        document.head.appendChild(script);

        return () => {
            const el = document.getElementById('schema-howto');
            if (el) el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="How RaffleKit Works — Run Giveaways in Minutes"
                description="Learn how to launch a viral giveaway with RaffleKit in 4 easy steps: create your contest, share your link, collect entries, and pick a winner fairly."
                canonical="https://rafflekit.app/how-it-works"
            />
            <LandingNav />

            {/* Hero Section */}
            <section className="pt-32 pb-20 bg-gray-50 text-center">
                <div className="container mx-auto px-4">
                    <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        How RaffleKit Works
                    </h1>
                    <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-10">
                        Create, share, collect entries, pick a winner. It's that simple.
                    </p>
                    <div className="flex justify-center gap-4">
                        <Button asChild size="lg" variant="accent" className="font-bold text-lg h-12 px-8 rounded-full">
                            <Link to="/admin?mode=signup">
                                Start Free
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* 4 Steps Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 max-w-4xl">
                    <div className="space-y-16">
                        {/* Step 1 */}
                        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16">
                            <div className="w-16 h-16 rounded-full border-2 border-primary bg-white flex items-center justify-center text-primary font-bold text-2xl flex-shrink-0 shadow-sm">
                                1
                            </div>
                            <div className="flex-[2] text-center md:text-left">
                                <h3 className="font-heading text-2xl font-bold text-gray-900 mb-4 flex items-center justify-center md:justify-start gap-2">
                                    <Gift className="w-6 h-6 text-primary" /> Create your contest
                                </h3>
                                <p className="text-gray-500 text-lg leading-relaxed">
                                    Set up your prize details, entry methods, and contest dates in just a few clicks. Choose from simple entry actions like visiting a webpage, following your social media accounts, or referring friends. You have full control over how users participate and earn their chance to win.
                                </p>
                            </div>
                            <div className="flex-1 min-w-0 flex justify-center items-center px-4" id="step1">
                                <CreateContest />
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16 md:flex-row-reverse">
                            <div className="w-16 h-16 rounded-full border-2 border-primary bg-white flex items-center justify-center text-primary font-bold text-2xl flex-shrink-0 shadow-sm">
                                2
                            </div>
                            <div className="flex-[2] text-center md:text-right">
                                <h3 className="font-heading text-2xl font-bold text-gray-900 mb-4 flex items-center justify-center md:justify-end gap-2">
                                    <Share2 className="w-6 h-6 text-primary" /> Share your link
                                </h3>
                                <p className="text-gray-500 text-lg leading-relaxed">
                                    Once published, you get a unique, mobile-optimized landing page link to share instantly with your audience across email and social media. Alternatively, you can embed the sleek contest widget directly onto your own website to capture traffic where it already lives.
                                </p>
                            </div>
                            <div className="flex-1 min-w-0 flex justify-center items-center px-4" id="step2">
                                <ShareYourLink />
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16">
                            <div className="w-16 h-16 rounded-full border-2 border-primary bg-white flex items-center justify-center text-primary font-bold text-2xl flex-shrink-0 shadow-sm">
                                3
                            </div>
                            <div className="flex-[2] text-center md:text-left">
                                <h3 className="font-heading text-2xl font-bold text-gray-900 mb-4 flex items-center justify-center md:justify-start gap-2">
                                    <Users className="w-6 h-6 text-primary" /> Collect entries
                                </h3>
                                <p className="text-gray-500 text-lg leading-relaxed">
                                    Watch your participant list grow as entries roll in automatically. Our viral referral system incentivizes users to share with their friends for bonus entries, multiplying your reach. Every entry is securely verified and tracked in real-time on your dashboard.
                                </p>
                            </div>
                            <div className="flex-1 min-w-0 flex justify-center items-center px-4" id="step3">
                                <EntriesRollingIn />
                            </div>
                        </div>

                        {/* Step 4 */}
                        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16 md:flex-row-reverse">
                            <div className="w-16 h-16 rounded-full border-2 border-primary bg-white flex items-center justify-center text-primary font-bold text-2xl flex-shrink-0 shadow-sm">
                                4
                            </div>
                            <div className="flex-[2] text-center md:text-right">
                                <h3 className="font-heading text-2xl font-bold text-gray-900 mb-4 flex items-center justify-center md:justify-end gap-2">
                                    <Trophy className="w-6 h-6 text-primary" /> Pick a winner
                                </h3>
                                <p className="text-gray-500 text-lg leading-relaxed">
                                    When your contest timer runs out, use our built-in randomizer to seamlessly select a winner from all valid, fraud-checked entries. Afterward, you can export your entire list of participants as a CSV file to effortlessly import into your preferred marketing or CRM tools.
                                </p>
                            </div>
                            <div className="flex-1 min-w-0 flex justify-center items-center px-4" id="step4">
                                <WinnerSelection />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Simplicity vs Complexity */}
            <section className="py-20 bg-gray-50/50 text-center">
                <div className="container mx-auto px-4 max-w-3xl">
                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-8">
                        No complicated funnels. No confusion.
                    </h2>
                    <p className="text-gray-500 text-lg mb-12">
                        Other platforms make you jump through hoops just to launch a simple giveaway. We keep it straightforward so you can launch faster.
                    </p>
                </div>
            </section>

            {/* CTA Band */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-8">
                        Ready to run your first giveaway?
                    </h2>
                    <Button asChild size="lg" variant="accent" className="font-bold text-lg h-14 px-10 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all">
                        <Link to="/admin?mode=signup">
                            Start Free
                        </Link>
                    </Button>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
};

export default HowItWorks;
